import {ERROR_PERMISSION_DENIED, ERROR_UNAUTHORIZED} from "@/lib/api/errorCodes.tsx";
import {graphqlUri} from "@/lib/api/graphqlEndpoint.ts";
import {captureMessageForReporting} from "@/lib/errorReporting.tsx";
import {ResponseStatus} from "@/lib/types/common.ts";
import {buildLoginUrlWithReturn} from "@/lib/utils/authUtils.ts";
import {ApolloLink, Observable, ServerError} from "@apollo/client";
import {setContext} from "@apollo/client/link/context/index";
import {onError} from "@apollo/client/link/error/index";
import {createUploadLink} from "apollo-upload-client";
import DOMPurify from "dompurify";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import {toast} from "react-hot-toast";

export const authLink = setContext((_, {headers, statusPageSlug}) => {
  // Don't send the dev infra basic-auth header for status page queries —
  // it would conflict with the page-specific basic auth handled by the browser.
  if (statusPageSlug) {
    return {headers};
  }
  return {
    headers: {
      ...headers,
      authorization: "Basic YWRtaW46dXB0aW1lMjAxNA==",
    },
  };
});

export const httpLink = createUploadLink({
  uri: (operation) => graphqlUri(operation.operationName),
  credentials: "include",
});

export const outdatedContractErrorLink = onError(({networkError}) => {
  if (networkError?.name === "ServerError" && (networkError as ServerError).statusCode === 400) {
    const result = (networkError as ServerError).result;
    const errors = get(result, "errors", []) || [];
    const error = errors
      .filter((e: undefined | {message: string}) => !isEmpty(e?.message))
      .map((e: {message?: string}) => e.message ?? "")
      .join("");
    captureMessageForReporting(error);
  }
});

export const unauthorizedErrorLink = onError(({graphQLErrors, networkError, operation}) => {
  if (operation.getContext().skipAuthError) {
    return;
  }

  if (graphQLErrors) {
    const hasUnauthorizedError = graphQLErrors.some((error) => error?.extensions?.code === ERROR_UNAUTHORIZED);

    if (hasUnauthorizedError) {
      const loginUrl = buildLoginUrlWithReturn();
      window.location.replace(loginUrl);
      return;
    }
  }

  if (networkError?.name === "ServerError") {
    const serverError = networkError as ServerError;

    const errors = get(serverError.result, "errors", []) || [];
    const hasUnauthorizedError = errors.some((error: {message?: string}) => error.message === "unauthorized");

    if (hasUnauthorizedError) {
      const loginUrl = buildLoginUrlWithReturn();
      window.location.replace(loginUrl);
      return;
    }

    if (serverError.statusCode === 401) {
      const loginUrl = buildLoginUrlWithReturn();
      window.location.replace(loginUrl);
      return;
    }
  }
});

export const noPermissionErrorToastLink = onError(({graphQLErrors, operation}) => {
  const context = operation.getContext();
  if (context?.noToast) {
    return;
  }
  if (graphQLErrors) {
    const hasPermissionError = graphQLErrors.some((error) => error?.extensions?.code === ERROR_PERMISSION_DENIED);
    if (hasPermissionError) {
      toast.error("Oops, seems like you don't have permission to perform this action.");
    }
  }
});

export const toastErrorLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  return forward(operation).map((response) => {
    if (!context?.noToast) {
      const djangoResponse = response?.data?.response;

      if (djangoResponse?.status === ResponseStatus.ERROR && djangoResponse?.message?.__all__) {
        const message = djangoResponse.message.__all__.join(", ");
        toast.error(<span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(message) || ""}} />);
      }
    }
    return response;
  });
});

export const changeCursorLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const observable = forward(operation);
    document.body.classList.add("loading");
    const subscription = observable.subscribe({
      next(value) {
        observer.next(value);
      },
      error(networkError) {
        observer.error(networkError);
      },
      complete() {
        observer.complete();
        document.body.classList.remove("loading");
      },
    });

    return () => subscription.unsubscribe();
  });
});
